import * as T from 'three';
import { makeNoise } from './noise';

/** Tileable wave normal map built from a sum of directional ripples. */
function waveNormals(size = 256, seed = 3) {
  const n = makeNoise(seed), h = new Float32Array(size * size), data = new Uint8Array(size * size * 4);
  const waves = Array.from({ length: 14 }, (_, i) => {
    const a = n.hash(i, 1) * Math.PI * 2, k = 2 + Math.floor(n.hash(i, 2) * (i < 5 ? 4 : 14));
    return { kx: Math.round(Math.cos(a) * k), kz: Math.round(Math.sin(a) * k), amp: 1 / (k + 1), phase: n.hash(i, 3) * 6.28 };
  });
  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    let v = 0; for (const w of waves) v += Math.sin((w.kx * x + w.kz * z) / size * Math.PI * 2 + w.phase) * w.amp;
    h[z * size + x] = v;
  }
  for (let z = 0; z < size; z++) for (let x = 0; x < size; x++) {
    const i = z * size + x, dx = h[z * size + (x + 1) % size] - h[z * size + (x + size - 1) % size], dz = h[((z + 1) % size) * size + x] - h[((z + size - 1) % size) * size + x];
    const v = new T.Vector3(-dx * 2.2, -dz * 2.2, 1).normalize();
    data[i * 4] = (v.x * .5 + .5) * 255; data[i * 4 + 1] = (v.y * .5 + .5) * 255; data[i * 4 + 2] = (v.z * .5 + .5) * 255; data[i * 4 + 3] = 255;
  }
  const t = new T.DataTexture(data, size, size, T.RGBAFormat);
  t.wrapS = t.wrapT = T.RepeatWrapping; t.minFilter = T.LinearMipmapLinearFilter; t.magFilter = T.LinearFilter; t.generateMipmaps = true; t.anisotropy = 8; t.needsUpdate = true;
  return t;
}

export class Water {
  mesh: T.Mesh; private uniforms: Record<string, T.IUniform>;
  constructor(level: number, shoreX: number, centreZ: number, depthAt: (x: number, z: number) => number, palette: { deep: string; shallow: string }) {
    const xs: number[] = []; let x = shoreX - 80, s = 2.5;
    while (x < shoreX + 5200) { xs.push(x); x += s; s *= 1.055; }
    const zs: number[] = []; for (let z = centreZ - 4200; z <= centreZ + 4200; z += 14) zs.push(z);
    const nx = xs.length, nz = zs.length, pos = new Float32Array(nx * nz * 3), depth = new Float32Array(nx * nz);
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) {
      const k = j * nx + i; pos[k * 3] = xs[i]; pos[k * 3 + 1] = level; pos[k * 3 + 2] = zs[j];
      depth[k] = Math.max(-2, level - depthAt(xs[i], zs[j]));
    }
    const index = new Uint32Array((nx - 1) * (nz - 1) * 6); let w = 0;
    for (let j = 0; j < nz - 1; j++) for (let i = 0; i < nx - 1; i++) { const a = j * nx + i, b = a + 1, d = a + nx, e = d + 1; index[w++] = a; index[w++] = d; index[w++] = b; index[w++] = b; index[w++] = d; index[w++] = e; }
    const geo = new T.BufferGeometry(); geo.setAttribute('position', new T.BufferAttribute(pos, 3)); geo.setAttribute('aDepth', new T.BufferAttribute(depth, 1));
    geo.setAttribute('normal', new T.BufferAttribute(new Float32Array(nx * nz * 3).map((_, i) => i % 3 === 1 ? 1 : 0), 3));
    geo.setIndex(new T.BufferAttribute(index, 1)); geo.computeBoundingSphere();
    this.uniforms = { tWaves: { value: waveNormals() }, uTime: { value: 0 }, uDeep: { value: new T.Color(palette.deep) }, uShallow: { value: new T.Color(palette.shallow) } };
    const material = new T.MeshStandardMaterial({ color: '#ffffff', roughness: .05, metalness: 0, transparent: true, depthWrite: true });
    material.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, this.uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float aDepth; uniform float uTime; varying float vDepth; varying vec3 vWaterWorld;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>
          vec4 wsp = modelMatrix * vec4( transformed, 1.0 );
          float swell = sin( wsp.x * 0.045 + uTime * 0.9 ) * 0.22 + sin( wsp.z * 0.06 - uTime * 0.7 + wsp.x * 0.02 ) * 0.16;
          transformed.y += swell * smoothstep( 0.0, 6.0, aDepth ); vDepth = aDepth; vWaterWorld = wsp.xyz;`);
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D tWaves; uniform float uTime; uniform vec3 uDeep; uniform vec3 uShallow; varying float vDepth; varying vec3 vWaterWorld; float wFoam;')
        .replace('#include <map_fragment>', `
          vec2 wuv = vWaterWorld.xz;
          vec3 w1 = texture2D( tWaves, wuv * 0.021 + vec2( uTime * 0.012, uTime * 0.007 ) ).xyz * 2.0 - 1.0;
          vec3 w2 = texture2D( tWaves, wuv * 0.057 + vec2( - uTime * 0.018, uTime * 0.021 ) ).xyz * 2.0 - 1.0;
          vec3 w3 = texture2D( tWaves, wuv * 0.0045 + vec2( uTime * 0.003, - uTime * 0.002 ) ).xyz * 2.0 - 1.0;
          float wd = length( vWaterWorld - cameraPosition );
          float calm = mix( 1.0, 0.25, smoothstep( 60.0, 900.0, wd ) );
          vec3 wn = normalize( vec3( ( w1.x + w2.x * 0.6 + w3.x * 1.2 ) * calm, 1.0, ( w1.y + w2.y * 0.6 + w3.y * 1.2 ) * calm ) * vec3( 0.38, 1.0, 0.38 ) );
          float shore = smoothstep( 0.0, 9.0, vDepth );
          wFoam = ( 1.0 - smoothstep( 0.0, 1.6, vDepth + ( w2.x + w1.y ) * 0.6 + sin( uTime * 1.3 + vDepth * 2.5 ) * 0.35 ) ) * step( -0.5, vDepth );
          diffuseColor.rgb = mix( uShallow, uDeep, shore );
          diffuseColor.rgb = mix( diffuseColor.rgb, vec3( 0.92, 0.95, 0.96 ), wFoam * 0.85 );
          diffuseColor.a = mix( 0.55, 1.0, smoothstep( 0.0, 3.5, vDepth ) );`)
        .replace('#include <roughnessmap_fragment>', 'float roughnessFactor = mix( 0.045, 0.6, wFoam );')
        .replace('#include <normal_fragment_maps>', 'normal = normalize( ( viewMatrix * vec4( wn, 0.0 ) ).xyz );');
    };
    material.customProgramCacheKey = () => 'tq-water';
    this.mesh = new T.Mesh(geo, material); this.mesh.name = 'water'; this.mesh.receiveShadow = true; this.mesh.renderOrder = 1;
  }
  update(time: number) { this.uniforms.uTime.value = time; }
  dispose() { this.mesh.geometry.dispose(); (this.mesh.material as T.Material).dispose(); (this.uniforms.tWaves.value as T.Texture).dispose(); }
}
